import React, { useEffect, useState } from 'react';
import {
  aopApi, agentForWorkflow, agentLabel, formatTokens, formatWhen,
  prettyActionType, runDuration, workflowLabel, WORKFLOWS,
  platformForWorkflow, platformLabel,
} from '../aopApi';

/* ── inline icons ─────────────────────────────────────────────── */
const Icon = {
  user: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
  zap: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>,
  db: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/></svg>,
  play: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>,
  chevRight: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>,
  sparkles: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/></svg>,
};

const eventColor = (type) => {
  if (type.includes('failed')) return '#ff625d';
  if (type.includes('completed') || type.includes('decided')) return '#34c759';
  if (type.startsWith('approval')) return '#f97316';
  if (type.startsWith('agent')) return '#8867b2';
  if (type.startsWith('report')) return '#10b981';
  return '#4a8ff7';
};

function AgentTag({ agent }) {
  return <span className={`aop-agent-tag ${agent}`}>{agentLabel(agent)}</span>;
}

/* ── main component ──────────────────────────────────────────── */
export default function Overview({ onViewChange, onRunClick }) {
  const [status, setStatus] = useState(null);
  const [runs, setRuns] = useState([]);
  const [pending, setPending] = useState([]);
  const [audit, setAudit] = useState([]);
  const [latestBrief, setLatestBrief] = useState(null);
  const [error, setError] = useState(null);
  const [triggerWf, setTriggerWf] = useState('');
  const [triggerLoading, setTriggerLoading] = useState(false);
  const [triggerDone, setTriggerDone] = useState(false);

  const load = () => {
    Promise.all([
      aopApi.status(),
      aopApi.runs({ limit: 50 }),
      aopApi.pendingActions({ limit: 50 }),
      aopApi.audit({ limit: 10 }),
      aopApi.reports({ limit: 10 }),
    ])
      .then(([statusRes, runsRes, pendingRes, auditRes, reportsRes]) => {
        if (statusRes.success) setStatus(statusRes.status);
        if (runsRes.success) setRuns(runsRes.runs || []);
        if (pendingRes.success) setPending(pendingRes.actions || []);
        if (auditRes.success) setAudit(auditRes.events || []);
        if (reportsRes.success) {
          setLatestBrief((reportsRes.reports || []).find((r) => r.type.startsWith('orchestrator')) || null);
        }
        setError(null);
      })
      .catch((err) => setError(err.message));
  };

  useEffect(load, []);

  const decide = async (id, decision) => {
    try {
      const res = decision === 'approved' ? await aopApi.approve(id) : await aopApi.reject(id);
      if (!res.success) throw new Error(res.error || 'Decision failed');
      setPending((prev) => prev.map((p) => (p._id === id ? res.action : p)));
    } catch (err) {
      setError(err.message);
    }
  };

  const handleTrigger = async () => {
    if (!triggerWf) return;
    setTriggerLoading(true);
    try {
      const res = await aopApi.enqueue(triggerWf, {});
      if (!res.success) throw new Error(res.error || 'Enqueue failed');
      setTriggerDone(true);
      setTriggerWf('');
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setTriggerLoading(false);
    }
  };

  const pendingOpen = pending.filter((p) => p.status === 'pending');
  const tokensMtd = status ? status.tokensThisMonth : 0;
  const budget = status && status.monthlyTokenBudget;
  const stats = [
    { label: 'Workflow Runs', value: runs.length, sub: 'Recent', color: '#3c8cda' },
    { label: 'Pending Actions', value: pendingOpen.length, sub: 'Awaiting review', color: '#f0198c', topBorder: '#f0198c' },
    { label: 'Completed', value: runs.filter((r) => r.status === 'completed').length, sub: 'Recent runs', color: '#10b981' },
    { label: 'Failed', value: runs.filter((r) => r.status === 'failed').length, sub: 'Recent runs', color: '#ff625d' },
    { label: 'Tokens MTD', value: formatTokens(tokensMtd), sub: budget ? `of ${formatTokens(budget)} budget` : 'No budget set', color: '#8867b2' },
  ];

  const briefContent = latestBrief && typeof latestBrief.content === 'object' ? latestBrief.content : null;

  return (
    <div>
      {/* ── page header ── */}
      <div className="aop-page-header">
        <div>
          <h1 className="aop-page-title">Overview</h1>
          <p className="aop-page-subtitle">Live workflow, approval, and system state from the AOP API.</p>
        </div>
        <div className="aop-ov-extras">
          <div className="aop-ov-badge">{Icon.user} {status ? (status.writePolicy === 'human_approval' ? 'Human Approval' : status.writePolicy) : '…'}</div>
          <div className="aop-ov-badge muted">
            {Icon.zap} {status ? `${status.modelProvider}${status.dryRun ? ' (Dry Run)' : ''}` : '…'}
          </div>
          <div className="aop-ov-token">
            <div className="aop-ov-token-row">{Icon.db} Token Usage MTD</div>
            <div className="aop-ov-token-sub">{formatTokens(tokensMtd)}{budget ? ` / ${formatTokens(budget)}` : ''}</div>
            <div className="aop-ov-token-track">
              <div className="aop-ov-token-fill" style={{ width: budget ? `${Math.min(100, (tokensMtd / budget) * 100)}%` : '0%' }} />
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div style={{ background: 'rgba(255,98,93,0.1)', border: '1px solid rgba(255,98,93,0.25)', borderRadius: 6, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: 'var(--red)' }}>
          {error}
        </div>
      )}

      {/* ── stat cards ── */}
      <div className="aop-stats-row">
        {stats.map((s) => (
          <div key={s.label} className="aop-stat-card" style={s.topBorder ? { borderTop: `3px solid ${s.topBorder}` } : {}}>
            <div className="aop-stat-card-header">
              <span className="aop-stat-card-label">{s.label}</span>
            </div>
            <div className="aop-stat-value" style={{ color: s.color }}>{s.value}</div>
            <div className="aop-stat-delta up">
              <span className="aop-stat-delta-sub">{s.sub}</span>
            </div>
          </div>
        ))}
      </div>

      {/* ── recent workflow runs ── */}
      <div className="aop-section">
        <div className="aop-section-header">
          <span className="aop-section-title">Recent Workflow Runs</span>
          <span className="aop-view-all" onClick={() => onViewChange('workflow-runs')}>View All →</span>
        </div>
        <div className="aop-card" style={{ padding: 0, overflow: 'hidden' }}>
          {runs.length === 0 ? (
            <div className="aop-empty">No workflow runs yet — use Manual Trigger or start the AOP worker</div>
          ) : runs.slice(0, 5).map((r, i, arr) => (
            <div
              key={r._id}
              className="aop-ov-run-row"
              style={{ borderBottom: i < arr.length - 1 ? '1px solid var(--border)' : 'none' }}
              onClick={() => onRunClick(r._id)}
            >
              <div className={`aop-ov-run-dot ${r.status}`} />
              <div className="aop-ov-run-name">{workflowLabel(r.workflow)}</div>
              <span className={`aop-badge ${platformForWorkflow(r.workflow)}`}>{platformLabel(platformForWorkflow(r.workflow))}</span>
              <AgentTag agent={agentForWorkflow(r.workflow)} />
              <span className="aop-ov-run-trigger">{r.triggerType}</span>
              <span className={`aop-badge ${r.status}`}>{r.status}</span>
              <span className="aop-ov-run-dur">{runDuration(r)}</span>
              <span style={{ color: 'var(--text-dim)' }}>{Icon.chevRight}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── pending actions (top) ── */}
      <div className="aop-section">
        <div className="aop-section-header">
          <span className="aop-section-title">Pending Actions</span>
          <span className="aop-view-all" onClick={() => onViewChange('pending-actions')}>
            View All ({pendingOpen.length}) →
          </span>
        </div>
        <div className="aop-card" style={{ padding: 0, overflow: 'hidden' }}>
          {pendingOpen.length === 0 ? (
            <div className="aop-empty">No actions awaiting review</div>
          ) : pendingOpen.slice(0, 4).map((p, i, arr) => (
            <div
              key={p._id}
              className="aop-ov-pending-row"
              style={{ borderBottom: i < arr.length - 1 ? '1px solid var(--border)' : 'none' }}
            >
              <span className={`aop-badge ${p.riskLevel}`}>{String(p.riskLevel || '').toUpperCase()}</span>
              <div className="aop-ov-pending-action">{prettyActionType(p.actionType)}</div>
              <AgentTag agent={p.sourceAgent} />
              <span className="aop-ov-run-trigger">{p.targetSystem}</span>
              <span className="aop-ov-run-trigger" style={{ minWidth: 48 }}>{formatWhen(p.createdAt)}</span>
              <div className="aop-ov-pending-btns">
                <button className="aop-btn approve sm" onClick={() => decide(p._id, 'approved')}>Approve</button>
                <button className="aop-btn reject sm" onClick={() => decide(p._id, 'rejected')}>Reject</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── middle grid: brief + trigger ── */}
      <div className="aop-grid-2" style={{ marginBottom: 20 }}>
        {/* Latest ChrisGPT Brief */}
        <div className="aop-card">
          <div className="aop-card-header">
            <span className="aop-card-title">{Icon.sparkles} ChrisGPT Brief</span>
            <span className="aop-view-all" onClick={() => onViewChange('reports')}>View Reports →</span>
          </div>
          <div className="aop-card-body">
            {!latestBrief ? (
              <div className="aop-empty">No briefs generated yet</div>
            ) : (
              <>
                <div style={{ fontSize: 10, color: 'var(--text-dim)', marginBottom: 10 }}>
                  {formatWhen(latestBrief.createdAt)} · {latestBrief.title || latestBrief.type}
                </div>
                {briefContent && Array.isArray(briefContent.risks) && (
                  <div className="aop-ov-brief-stats">
                    {[
                      ['Risks', briefContent.risks.length],
                      ['Focus Items', Array.isArray(briefContent.focus) ? briefContent.focus.length : 0],
                      ['Escalations', Array.isArray(briefContent.escalations) ? briefContent.escalations.length : 0],
                      ['Confidence', latestBrief.confidence != null ? `${Math.round(latestBrief.confidence * 100)}%` : '—'],
                    ].map(([k, v]) => (
                      <div key={k} className="aop-ov-brief-stat">
                        <div className="aop-ov-brief-stat-val">{v}</div>
                        <div className="aop-ov-brief-stat-key">{k}</div>
                      </div>
                    ))}
                  </div>
                )}
                <p style={{ fontSize: 12, color: 'var(--text-sec)', lineHeight: 1.6, margin: '10px 0 0' }}>
                  {briefContent && briefContent.summary ? briefContent.summary : JSON.stringify(latestBrief.content).slice(0, 240)}
                </p>
              </>
            )}
          </div>
        </div>

        {/* Manual Trigger quick-access */}
        <div className="aop-card">
          <div className="aop-card-header">
            <span className="aop-card-title">{Icon.play} Manual Trigger</span>
            {status && status.dryRun && (
              <span className="aop-badge" style={{ background: 'rgba(249,115,22,0.12)', color: 'var(--orange)' }}>DRY RUN</span>
            )}
          </div>
          <div className="aop-card-body">
            {triggerDone && (
              <div style={{ background: 'rgba(52,199,89,0.1)', border: '1px solid rgba(52,199,89,0.2)', borderRadius: 6, padding: '8px 12px', marginBottom: 12, fontSize: 11, color: 'var(--green)' }}>
                ✓ Workflow enqueued. Check Workflow Runs for status.
              </div>
            )}
            <div className="aop-form-group">
              <label className="aop-form-label">Workflow</label>
              <select className="aop-form-select" value={triggerWf} onChange={(e) => { setTriggerWf(e.target.value); setTriggerDone(false); }}>
                <option value="">Select a workflow…</option>
                {WORKFLOWS.filter((w) => !w.needsEntity).map((w) => (
                  <option key={w.id} value={w.id}>{w.label}</option>
                ))}
              </select>
              <div style={{ marginTop: 6, fontSize: 10, color: 'var(--text-dim)' }}>
                Workflows that need an Opportunity ID live in <span className="aop-view-all" style={{ fontSize: 10 }} onClick={() => onViewChange('manual-trigger')}>Manual Trigger →</span>
              </div>
            </div>
            <button
              className="aop-btn trigger"
              style={{ width: '100%', justifyContent: 'center', opacity: !triggerWf ? 0.45 : 1 }}
              disabled={!triggerWf || triggerLoading}
              onClick={handleTrigger}
            >
              {Icon.play} {triggerLoading ? 'Enqueueing…' : 'Trigger Workflow'}
            </button>
          </div>
        </div>
      </div>

      {/* ── bottom grid: health + activity ── */}
      <div className="aop-grid-2">
        {/* System Health */}
        <div className="aop-card">
          <div className="aop-card-header">
            <span className="aop-card-title">System Health</span>
            {status && (
              <span className={`aop-badge ${status.jobs.failed > 0 ? 'medium' : 'completed'}`}>
                {status.jobs.failed > 0 ? `${status.jobs.failed} failed jobs` : 'Healthy'}
              </span>
            )}
          </div>
          <div className="aop-card-body">
            {!status ? (
              <div className="aop-empty">Loading…</div>
            ) : [
              { label: 'Worker Enabled', value: status.enabled ? 'Yes' : 'No (AOP_ENABLED=0)', color: status.enabled ? 'var(--green)' : 'var(--orange)' },
              { label: 'Queued Jobs', value: String(status.jobs.pending), color: 'var(--text)' },
              { label: 'Claimed Jobs', value: String(status.jobs.claimed), color: 'var(--blue)' },
              { label: 'Failed Jobs', value: String(status.jobs.failed), color: status.jobs.failed > 0 ? 'var(--red)' : 'var(--green)' },
              { label: 'Model Provider', value: status.modelProvider, color: 'var(--purple)' },
              { label: 'Write Policy', value: status.writePolicy === 'human_approval' ? 'Human Approval' : status.writePolicy, color: 'var(--blue)' },
              { label: 'AOP Mode', value: status.dryRun ? 'DRY RUN' : 'LIVE', color: status.dryRun ? 'var(--orange)' : 'var(--green)' },
              { label: 'Last Completed Run', value: status.lastCompletedRun ? `${workflowLabel(status.lastCompletedRun.workflow)} · ${formatWhen(status.lastCompletedRun.completedAt)}` : '—', color: 'var(--text)' },
            ].map((row) => (
              <div key={row.label} className="aop-health-row">
                <span className="aop-health-label">{row.label}</span>
                <span className="aop-health-value" style={{ color: row.color }}>{row.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="aop-card">
          <div className="aop-card-header">
            <span className="aop-card-title">Recent Activity</span>
            <span className="aop-view-all" onClick={() => onViewChange('audit-log')}>Audit Log →</span>
          </div>
          <div style={{ padding: '8px 0' }}>
            {audit.length === 0 ? (
              <div className="aop-empty">No activity yet</div>
            ) : audit.map((ev, i) => (
              <div key={ev._id} className="aop-audit-event">
                <div className="aop-audit-dot-col">
                  <div className="aop-audit-dot" style={{ background: eventColor(ev.type) }} />
                  {i < audit.length - 1 && <div className="aop-audit-line" />}
                </div>
                <div className="aop-audit-body">
                  <div className="aop-audit-desc">{ev.message}</div>
                  <div className="aop-audit-meta"><span>{formatWhen(ev.createdAt)}</span></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
